import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Slider,
  Snackbar,
  Alert,
  TextField,
  Typography,
  LinearProgress,
  CircularProgress,
  Stack,
  useTheme,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";

import * as c from "../../../../constants";
import { useTranslation } from "../../../../language";
import ButtonForm from "../../../../components/Button/ButtonForm";

const mappedPrefill = (val) => {
  if (!val) return "";
  if (val === "M") return "male";
  if (val === "F") return "female";
  return val;
};

// labVisitId is always resolved by the parent (StateQuest) — no fallback needed here.
export default function SpeakQuest({ labVisitId, onSubmitSuccess }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const questionRefs = useRef({});

  // ── Fetch questionnaire with most recent answers ──────────────────────────
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${c.API_URL}/api/questionnaire/latest-with-answers/`)
      .then((res) => {
        setQuestionnaire(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ── Normalize questions ────────────────────────────────────────────────────
  const questions = useMemo(() => {
    if (!questionnaire) return [];

    return questionnaire.questions.map((qq) => ({
      id: qq.question.id,
      key: qq.id,
      code: qq.question.code,
      text: qq.question.text,
      question_type: qq.question.question_type,
      options: qq.question.options || [],
      required: qq.required,
      order: qq.order,
      depends_on_code: qq.depends_on_code,
      depends_on_value: qq.depends_on_value,
      prefilled_value: qq.question.prefilled_value ?? null,
    }));
  }, [questionnaire]);

  // ── Prefill answers from DB values ────────────────────────────────────────
  useEffect(() => {
    if (!questionnaire) return;

    const initialAnswers = {};
    questionnaire.questions.forEach((qq) => {
      const prefill = qq.question.prefilled_value;
      if (prefill !== null && prefill !== undefined) {
        initialAnswers[qq.question.code] = mappedPrefill(prefill);
      }
    });

    setAnswers(initialAnswers);
  }, [questionnaire]);

  // ── Answer handler ────────────────────────────────────────────────────────
  const handleChange = (code, value) =>
    setAnswers((prev) => ({ ...prev, [code]: value }));

  // ── Conditional visibility ────────────────────────────────────────────────
  const isQuestionVisible = (q) => {
    if (!q.depends_on_code) return true;
    const parentValue = answers[q.depends_on_code];
    if (!parentValue) return false;

    const parentValues = Array.isArray(parentValue) ? parentValue : [parentValue];
    const dependsValues = Array.isArray(q.depends_on_value)
      ? q.depends_on_value
      : [q.depends_on_value];

    return parentValues.some((val) => dependsValues.includes(val));
  };

  const visibleQuestions = questions.filter(isQuestionVisible);

  // ── Progress ──────────────────────────────────────────────────────────────
  const isAnswered = (q) => {
    const value = answers[q.code];
    if (q.question_type === "multi") return Array.isArray(value) && value.length > 0;
    return value !== undefined && value !== null && value !== "";
  };

  const answeredCount = visibleQuestions.filter(isAnswered).length;
  const progress =
    visibleQuestions.length > 0
      ? Math.round((answeredCount / visibleQuestions.length) * 100)
      : 0;

  // ── Scroll to first unanswered ────────────────────────────────────────────
  const scrollToFirstUnanswered = () => {
    const first = visibleQuestions.find((q) => !isAnswered(q));
    if (first && questionRefs.current[first.code]) {
      questionRefs.current[first.code].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (answeredCount !== visibleQuestions.length) {
      scrollToFirstUnanswered();
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post(`${c.API_URL}/api/existing/questionnaire/submit/`, {
        questionnaire: questionnaire.id,
        lab_visit_id: labVisitId,
        answers: Object.entries(answers).map(([code, value]) => {
          const q = questions.find((q) => q.code === code);
          return {
            question: q.id,
            value: q.question_type === "boolean" ? value === "true" : value,
          };
        }),
      });

      setShowSuccess(true);

      // Advance workflow state
      await axios.patch(`${c.API_URL}/api/user-lab-visits/${labVisitId}/`, {
        state: 4,
      });

      setTimeout(() => {
        onSubmitSuccess?.();
      }, 1500);
    } catch (err) {
      setSubmitError(
        err.response?.data?.message || t("val_server_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render question ───────────────────────────────────────────────────────
  const renderQuestion = (q, idx) => {
    const accentColor = idx % 2 === 0 ? theme.palette.tertiary.C300 : theme.palette.quartiary.C300;

    return (
      <Box
        key={q.key}
        sx={{
          border: "1px solid",
          borderRadius: 4,
          overflow: "hidden",
          borderLeft: `10px solid ${accentColor}`,
          mb: 1.5,
          p: 2,
          ...(theme.palette.mode === "light"
            ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
            : {
                bgcolor: "greydark.C900",
                borderColor: "greydark.C400",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
              }),
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          ref={(el) => (questionRefs.current[q.code] = el)}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: `2px solid ${accentColor}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: "2px",
            }}
          >
            <Typography
              variant="number4"
              sx={{ color: accentColor, fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
            >
              {idx + 1}
            </Typography>
          </Box>

          <FormControl fullWidth>
            <FormLabel>
              {q.text} {q.required && "*"}
            </FormLabel>

            {/* TEXT */}
            {q.question_type === "text" && (
              <TextField
                multiline
                required={q.required}
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, e.target.value)}
                sx={{ mt: 2, mb: 3 }}
              />
            )}

            {/* NUMBER */}
            {q.question_type === "number" && (
              <TextField
                type="number"
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, Number(e.target.value))}
              />
            )}

            {/* DATE */}
            {q.question_type === "date" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={answers[q.code] ? dayjs(answers[q.code], "YYYY-MM-DD", true) : null}
                  onChange={(val) => handleChange(q.code, val ? val.format("YYYY-MM-DD") : "")}
                  slotProps={{
                    textField: { fullWidth: true, sx: { mt: 2, mb: 3 }, format: "YYYY-MM-DD" },
                  }}
                />
              </LocalizationProvider>
            )}

            {/* SINGLE */}
            {q.question_type === "single" && (
              <RadioGroup
                value={answers[q.code] ? mappedPrefill(answers[q.code]) : mappedPrefill(q.prefilled_value)}
                onChange={(e) => handleChange(q.code, e.target.value)}
              >
                {q.options.map((opt) => (
                  <FormControlLabel key={opt.value} value={opt.value} control={<Radio />} label={opt.label} />
                ))}
              </RadioGroup>
            )}

            {/* MULTI */}
            {q.question_type === "multi" &&
              q.options.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={(answers[q.code] || []).includes(opt.value)}
                      onChange={(e) => {
                        const current = answers[q.code] || [];
                        handleChange(
                          q.code,
                          e.target.checked ? [...current, opt.value] : current.filter((v) => v !== opt.value)
                        );
                      }}
                    />
                  }
                  label={opt.label}
                />
              ))}

            {/* BOOLEAN */}
            {q.question_type === "boolean" && (
              <RadioGroup
                value={answers[q.code] || ""}
                onChange={(e) => handleChange(q.code, e.target.value.toLowerCase())}
              >
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.id}
                    value={String(opt.value).toLowerCase()}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}

                {!q.required && (
                  <FormControlLabel value="" control={<Radio />} label={t("onb_quest_no_answer")} />
                )}
              </RadioGroup>
            )}

            {/* SCALE */}
            {q.question_type === "scale" && (
              <Slider
                min={1}
                max={10}
                step={1}
                marks
                value={answers[q.code] ?? 1}
                onChange={(_, value) => handleChange(q.code, value)}
                valueLabelDisplay="auto"
                sx={{
                  mt: 3,
                  "& .MuiSlider-valueLabel": { fontSize: "1.2rem", fontWeight: 600 },
                  "& .MuiSlider-markLabel": { fontSize: "1rem" },
                }}
              />
            )}
          </FormControl>
        </Stack>
      </Box>
    );
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        px: { xs: 2, sm: 4 },
      }}
    >
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
        {t("onb_quest_form_subtitle")}
      </Typography>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, bgcolor: "tertiary.main" }} />
      <Typography variant="subtitle2" align="center" fontWeight={400} sx={{ mb: 4 }}>
        {answeredCount} / {visibleQuestions.length} {t("onb_quest_answered_label")}
      </Typography>

      {visibleQuestions.map(renderQuestion)}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <ButtonForm
        name={submitting ? <CircularProgress size={18} color="inherit" /> : t("onb_quest_submit")}
        onClick={handleSubmit}
        disabled={submitting}
      />

      <Snackbar
        open={showSuccess}
        autoHideDuration={1500}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "50%", transform: "translateY(-50%)" }}
      >
        <Alert severity="success">
          <Typography variant="h6">{t("onb_quest_submit_success")}</Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
}
